import { useEffect, useState } from 'react';

// AntD renders a full-viewport mask for every blocking overlay (Drawer / Modal).
// Maskless popups (dropdowns, selects, tooltips, toasts, popovers, anchored menus)
// never render these, so watching for them alone tells us when the page is "blocked"
// without maintaining an allow-list of components.
// The Listings V2 onboarding tour is not an AntD overlay but covers the page the same way, so it
// opts in by stamping its own root with this class.
const BLOCKING_MASK_SELECTOR = '.ant-drawer-mask, .ant-modal-mask, .listings-v2-tour-mask';

const EMPTY_STATE = { hasMask: false, isOpen: false, topZIndex: 0, maskBackground: '' };

const isMaskVisible = (mask) => {
  const { display, visibility } = window.getComputedStyle(mask);
  return display !== 'none' && visibility !== 'hidden';
};

// AntD animates masks with rc-motion, which stamps a "-leave" class on the element the moment
// the exit animation begins and keeps the mask mounted until the animation ends. Treating a
// leaving mask as already closed lets the rail recover the instant the overlay starts closing,
// in step with the content overlay fading, instead of waiting the full ~250ms for DOM removal.
const isLeaving = (mask) => mask.className.includes('-leave');

// Reads the current blocking-overlay state straight from the DOM. When several masks are stacked
// (e.g. a modal opened from inside a drawer) we key off the topmost one so the rail scrim matches
// the layer the user is actually looking at.
const readBlockingState = () => {
  const masks = Array.from(document.querySelectorAll(BLOCKING_MASK_SELECTOR)).filter(isMaskVisible);

  if (!masks.length) {
    return EMPTY_STATE;
  }

  const openMasks = masks.filter((mask) => !isLeaving(mask));
  // Rank among the still-open masks; fall back to any present mask while everything is leaving so
  // the fading scrim still has a colour/z-index to render from during its exit transition.
  const ranked = openMasks.length ? openMasks : masks;

  let topMask = ranked[0];
  let topZIndex = parseInt(window.getComputedStyle(topMask).zIndex, 10) || 0;

  ranked.forEach((mask) => {
    const zIndex = parseInt(window.getComputedStyle(mask).zIndex, 10) || 0;
    if (zIndex >= topZIndex) {
      topZIndex = zIndex;
      topMask = mask;
    }
  });

  return {
    // A mask element is present (open or animating out) — keep the scrim mounted so it can fade.
    hasMask: true,
    // A mask is actively open (not animating out) — drives the rail dim + inert state.
    isOpen: openMasks.length > 0,
    topZIndex,
    // Copy the exact colour/opacity of the topmost mask so the rail dims to the same shade as the
    // content overlay (drawer ~0.45 vs modal 0.9) with no hardcoding.
    maskBackground: window.getComputedStyle(topMask).backgroundColor,
  };
};

const areStatesEqual = (a, b) =>
  a.hasMask === b.hasMask &&
  a.isOpen === b.isOpen &&
  a.topZIndex === b.topZIndex &&
  a.maskBackground === b.maskBackground;

// Watches the document for AntD drawer/modal masks and reports whether a blocking overlay is
// present/open, along with the metadata needed to render a matching, in-sync rail scrim.
function useBlockingOverlayState() {
  const [state, setState] = useState(() =>
    typeof document !== 'undefined' ? readBlockingState() : EMPTY_STATE,
  );

  useEffect(() => {
    let frame = null;

    const sync = () => {
      frame = null;
      setState((prev) => {
        const next = readBlockingState();
        return areStatesEqual(prev, next) ? prev : next;
      });
    };

    // Coalesce bursts of mutations into a single read per frame to avoid thrashing.
    const scheduleSync = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(sync);
    };

    // A mask may already be present when this mounts, so read once immediately.
    sync();

    const observer = new MutationObserver(scheduleSync);
    // Watch child add/remove (mask mount/unmount) and class changes (rc-motion enter/leave) so the
    // rail starts recovering the moment the exit animation begins, not when the mask is removed.
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => {
      observer.disconnect();
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, []);

  return state;
}

export default useBlockingOverlayState;
